<template>
  <component :is="component" v-bind="computedProps" @click="onClick">
    <slot :is-external="isExternal" />
  </component>
</template>

<script lang="ts" setup>
import { getPathFromUrl } from '~/helpers'

const EXTERNAL_URL_EXTENSIONS = ['jpg', 'png', 'svg', 'pdf']
const INTERNAL_SITES = [
  'zuerich.com',
  'zuerich.ddev.site',
  'staging.zuerich.com',
  'staging-5em2ouy-wib25giar74uy.eu-4.platformsh.site',
  'meeting.zuerich.com',
  'meeting.ddev.site',
  'meeting.staging-5em2ouy-wib25giar74uy.eu-4.platformsh.site',
  'zt.zuerich.com',
  'zt.ddev.site',
  'zt.staging-5em2ouy-wib25giar74uy.eu-4.platformsh.site',
]

const { activeHostname } = useSiteContext()

function getUrlExtension(url: string): string {
  try {
    const parts = url.split(/[#?]/)
    return parts[0].split('.').pop()?.trim() || ''
  } catch {
    return ''
  }
}

const props = withDefaults(
  defineProps<{
    to?: string
    target?: string
  }>(),
  {
    to: '',
    target: '',
  },
)

const route = useRoute()
const router = useRouter()

const href = computed(() => {
  if (!props.to) {
    return ''
  }
  if (props.to.includes(activeHostname)) {
    return getPathFromUrl(props.to)
  }
  if (props.to.startsWith('www')) {
    return 'https://' + props.to
  }
  return props.to
})

const component = computed(() => (href.value ? 'a' : 'div'))

const urlExtension = computed(() => getUrlExtension(href.value).toLowerCase())
const isInternal = computed(() =>
  INTERNAL_SITES.some((site) => href.value.includes(site)),
)

const isDrupalRouted = computed(
  () => href.value.includes('/admin/') || href.value.includes('card-shop'),
)

const isExternal = computed(
  () =>
    (!isInternal.value && href.value.startsWith('http')) ||
    href.value.startsWith('mailto') ||
    href.value.startsWith('tel') ||
    EXTERNAL_URL_EXTENSIONS.includes(urlExtension.value),
)

const isActive = computed(() => href.value === route.path)

const computedProps = computed(() => {
  /* eslint-disable  @typescript-eslint/no-explicit-any */
  const computedProps: Record<string, any> = {
    class: [],
  }
  if (href.value) {
    computedProps.href = href.value
    if (isExternal.value) {
      computedProps.target = '_blank'
    }
  }
  if (isActive.value) {
    computedProps.class.push('nuxt-link-exact-active')
  }
  return computedProps
})

function onClick(e: Event) {
  if (isExternal.value || isInternal.value || isDrupalRouted.value) {
    return
  }
  e.preventDefault()
  router.push(href.value)
}
</script>
